import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  Lock,
  Play,
  Pause,
  RotateCcw,
  Sparkles,
  ShieldAlert,
  ClipboardList,
  Beaker as BeakerIcon,
  Trophy,
  AlertTriangle,
} from 'lucide-react';
import LabBench from './LabBench';
import { useAnimationFrame } from '../viz/useAnimationFrame';
import {
  cueIsActive,
  cueHighlights,
  usePrefersReducedMotion,
  useMountSequence,
  NowYouTry,
  VIZ_BTN_PRIMARY,
  VIZ_BTN_GHOST,
  VIZ_FOCUS,
  type VizCue,
} from '../viz/vizShared';
import {
  Beaker,
  Burette,
  PHMeter,
  Spectrophotometer,
  TestTube,
  type BeakerProps,
  type BuretteProps,
  type PHMeterProps,
  type SpectrophotometerProps,
  type TestTubeProps,
} from '../lab';

// ─────────────────────────────────────────────────────────────────────────────
// ExperimentRunner — a config-driven, guided VIRTUAL EXPERIMENT ENGINE.
//
// Drives an ordered multi-step lab procedure: apparatus setup → sequential,
// optionally-GATED steps → live observations + a data-collection table (auto or
// learner entry) → a computed RESULT compared against the expected value with
// pass/fail + error %, plus safety notes and a completion summary that fires
// onComplete.
//
// Three archetypes are recognised via `config.kind`:
//   • 'titration'   — burette-driven; live volume scrubber, color-change endpoint
//   • 'gravimetric' — precipitate / mass-by-difference; settling + dry-and-weigh
//   • 'observation' — qualitative reaction watching (default fallback)
// The archetype only changes the live-apparatus animation + the default result
// formula; the step/checkpoint/data-table machinery is shared.
//
// CUE CONTRACT (optional, from the lecture player):
//   cue.step      → jump the active step (and mark all prior steps done)
//   cue.progress  → 0..1 over the whole procedure → derives the active step
//   cue.play      → auto-run the apparatus animation for the active step
//   cue.highlight → spotlight a step / apparatus by id, label or index
// When no cue is present the learner drives manually, with a one-shot self-demo.
//
// ExperimentConfig is WIDENED additively — every new field is optional, so the
// original { name, apparatus, steps } configs keep working unchanged.
// ─────────────────────────────────────────────────────────────────────────────

type ApparatusType = 'beaker' | 'burette' | 'ph_meter' | 'spectrophotometer' | 'test_tube';

export type ExperimentKind = 'titration' | 'gravimetric' | 'observation';

export interface ApparatusSpec {
  type: ApparatusType;
  /** Stable id so a step can target this apparatus (highlight / live update). */
  id?: string;
  /** Short caption shown under the piece on the bench. */
  caption?: string;
  props?: Record<string, unknown>;
}

/** A single procedure step. Strings are still accepted for back-compat. */
export interface ExperimentStep {
  /** Short imperative title (e.g. "Rinse and fill the burette"). */
  text: string;
  /** Optional supporting detail rendered under the title. */
  detail?: string;
  /** Stable id for cue.highlight targeting. */
  id?: string;
  /**
   * A gate the learner must satisfy before this step can complete. When set, the
   * step shows a live checkpoint and can't be ticked until met. Auto-satisfied
   * when the relevant live state crosses the target (e.g. endpoint reached).
   */
  checkpoint?: string;
  /** A live observation revealed once the step is reached. */
  observation?: string;
  /** Per-step safety callout. */
  safety?: string;
  /** Run the apparatus animation while this step is active (titration drip…). */
  animate?: boolean;
}

/** A column in the data-collection table. */
export interface DataColumn {
  key: string;
  label: string;
  unit?: string;
  /** 'auto' columns are filled by the engine from the simulation. */
  source?: 'auto' | 'entry';
}

/** One trial row of the data table; values may be pre-seeded. */
export interface DataRow {
  label?: string;
  values?: Record<string, number | string>;
}

export interface ResultSpec {
  /** Human label, e.g. "Concentration of HCl". */
  label?: string;
  /** Expected/accepted value used for the pass/fail + error %. */
  expected?: number;
  unit?: string;
  /** Acceptable absolute error % for a "pass" (default 5%). */
  tolerancePct?: number;
  /**
   * Formula id the engine uses to compute the result from collected data.
   * Recognised: 'titration_molarity' | 'mass_by_difference' | 'mean' | 'last'.
   * Falls back to the mean of the first auto/entry numeric column.
   */
  formula?: 'titration_molarity' | 'mass_by_difference' | 'mean' | 'last';
  /** Decimal places for display. */
  precision?: number;
}

export interface ExperimentConfig {
  name?: string;
  /** Experiment archetype — drives the live apparatus behaviour. */
  kind?: ExperimentKind;
  /** One-line objective shown above the procedure. */
  objective?: string;
  apparatus?: ApparatusSpec[];
  steps?: Array<string | ExperimentStep>;
  /** Standing safety notes for the whole experiment. */
  safety?: string[];
  /** Data-collection table definition. */
  dataColumns?: DataColumn[];
  dataRows?: DataRow[];
  /** Computed result spec (expected value, formula, tolerance). */
  result?: ResultSpec;
  /**
   * Titration tuning (kind === 'titration'): analyte concentration & volume,
   * titrant concentration. Used to derive the true endpoint volume and to seed
   * the auto data column.
   */
  titration?: {
    analyteConc?: number; // mol/L
    analyteVol?: number; // mL
    titrantConc?: number; // mol/L
    indicatorColor?: string; // endpoint color
  };
  /** Gravimetric tuning (kind === 'gravimetric'). */
  gravimetric?: {
    filterPaperMass?: number; // g (tare)
    precipitateMass?: number; // g (true)
  };
}

// ── apparatus rendering ──────────────────────────────────────────────────────

function renderApparatus(
  spec: ApparatusSpec,
  key: number,
  live: LiveState,
  highlighted: boolean,
) {
  // props come from JSONB config (untyped) — cast at this boundary.
  const base = (spec.props ?? {}) as Record<string, unknown>;
  let node: ReactNode = null;
  switch (spec.type) {
    case 'beaker': {
      const p: BeakerProps = {
        ...(base as BeakerProps),
        // live precipitate / clearing for gravimetric + reaction archetypes
        precipitate: live.precipitate ?? (base as BeakerProps).precipitate,
        muddy: live.muddy ?? (base as BeakerProps).muddy,
        active: highlighted || (base as BeakerProps).active,
      };
      node = <Beaker {...p} />;
      break;
    }
    case 'burette': {
      const p: BuretteProps = {
        ...(base as BuretteProps),
        volume: live.buretteVolume ?? (base as BuretteProps).volume,
        stopcock: live.dripping ? 'open' : ((base as BuretteProps).stopcock ?? 'closed'),
      };
      node = <Burette {...p} />;
      break;
    }
    case 'ph_meter': {
      const p: PHMeterProps = {
        ...(base as PHMeterProps),
        reading: live.ph ?? (base as PHMeterProps).reading,
        inSolution: live.probeIn ?? (base as PHMeterProps).inSolution,
      };
      node = <PHMeter {...p} />;
      break;
    }
    case 'spectrophotometer': {
      const p: SpectrophotometerProps = {
        ...(base as SpectrophotometerProps),
        absorbance: live.absorbance ?? (base as SpectrophotometerProps).absorbance,
        sampleIn: live.sampleIn ?? (base as SpectrophotometerProps).sampleIn,
      };
      node = <Spectrophotometer {...p} />;
      break;
    }
    case 'test_tube': {
      const p: TestTubeProps = {
        ...(base as TestTubeProps),
        active: highlighted || (base as TestTubeProps).active,
      };
      node = <TestTube {...p} />;
      break;
    }
    default:
      return null;
  }
  return (
    <div
      key={key}
      className="flex flex-col items-center gap-1 rounded-md p-1 transition-colors"
      style={highlighted ? { outline: '2px solid var(--ws-accent)', outlineOffset: 2 } : undefined}
    >
      {node}
      {spec.caption && (
        <span className="max-w-[8rem] truncate text-center text-[11px] text-ws-ink-3">{spec.caption}</span>
      )}
    </div>
  );
}

// ── live simulation state ────────────────────────────────────────────────────

interface LiveState {
  buretteVolume?: number;
  dripping?: boolean;
  ph?: number;
  probeIn?: boolean;
  precipitate?: number;
  muddy?: boolean;
  absorbance?: number;
  sampleIn?: boolean;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : NaN;
  return Number.isFinite(n) ? n : fallback;
}

function normalizeSteps(steps?: Array<string | ExperimentStep>): ExperimentStep[] {
  return (steps ?? []).map((s, i) =>
    typeof s === 'string' ? { text: s, id: `step-${i}` } : { id: `step-${i}`, ...s },
  );
}

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

// ── component ────────────────────────────────────────────────────────────────

interface Props {
  config: ExperimentConfig;
  onComplete?: (result: ExperimentResult) => void;
  /** Optional extra content rendered under the procedure rail. */
  aside?: ReactNode;
  /** Narration-synced cue from the lecture player (optional). */
  cue?: VizCue;
}

export interface ExperimentResult {
  stepsCompleted: number;
  total: number;
  computed?: number | null;
  expected?: number | null;
  errorPct?: number | null;
  passed?: boolean | null;
}

export default function ExperimentRunner({ config, onComplete, aside, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const steps = useMemo(() => normalizeSteps(config.steps), [config.steps]);
  const apparatus = config.apparatus ?? [];
  const kind: ExperimentKind = config.kind ?? 'observation';
  const safetyNotes = config.safety ?? [];

  // ── progress / step state ──────────────────────────────────────────────────
  const [done, setDone] = useState<Set<number>>(new Set());
  const [active, setActive] = useState(0); // index of the step in focus
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);
  const completedRef = useRef(false);

  // ── live simulation ────────────────────────────────────────────────────────
  // Titration endpoint volume from stoichiometry (Ca·Va = Cb·Vb).
  const titr = config.titration ?? {};
  const analyteConc = num(titr.analyteConc, 0.1);
  const analyteVol = num(titr.analyteVol, 25);
  const titrantConc = num(titr.titrantConc, 0.1);
  const endpointVol = titrantConc > 0 ? (analyteConc * analyteVol) / titrantConc : 25;
  const buretteCapacity = useMemo(() => {
    const b = apparatus.find((a) => a.type === 'burette');
    return num((b?.props as BuretteProps | undefined)?.maxVolume, 50);
  }, [apparatus]);

  const grav = config.gravimetric ?? {};
  const filterMass = num(grav.filterPaperMass, 0.85);
  const trueppt = num(grav.precipitateMass, 1.42);

  // Burette starts full; volume DELIVERED grows toward the endpoint.
  const [delivered, setDelivered] = useState(0); // mL delivered
  const deliveredRef = useRef(0);
  deliveredRef.current = delivered;
  const [settle, setSettle] = useState(0); // 0-1 gravimetric settling/drying
  const settleRef = useRef(0);
  settleRef.current = settle;

  const endpointReached = kind === 'titration' && delivered >= endpointVol - 0.05;
  const settled = kind === 'gravimetric' && settle >= 1;

  // Live pH along the (simplified) strong-acid/strong-base path.
  const livePh = useMemo(() => {
    const molA = analyteConc * (analyteVol / 1000);
    const molT = titrantConc * (delivered / 1000);
    const totalV = (analyteVol + delivered) / 1000;
    if (totalV <= 0) return 7;
    const net = molA - molT;
    if (net > 1e-9) return Math.max(0, -Math.log10(net / totalV));
    if (net < -1e-9) return Math.min(14, 14 + Math.log10(-net / totalV));
    return 7;
  }, [analyteConc, analyteVol, titrantConc, delivered]);

  const live: LiveState = useMemo(() => {
    switch (kind) {
      case 'titration':
        return {
          buretteVolume: Math.max(0, buretteCapacity - delivered),
          dripping: playing,
          ph: livePh,
          probeIn: true,
        };
      case 'gravimetric':
        return {
          precipitate: clamp01(settle),
          muddy: settle < 0.6,
        };
      default:
        return { absorbance: 0, sampleIn: false };
    }
  }, [kind, buretteCapacity, delivered, playing, livePh, settle]);

  // ── advance the simulation while playing ───────────────────────────────────
  useAnimationFrame((dt) => {
    if (kind === 'titration') {
      let next = deliveredRef.current;
      // slow near the endpoint so the color flip is visible
      const near = Math.abs(next - endpointVol) < endpointVol * 0.1;
      const rate = near ? endpointVol * 0.08 : endpointVol * 0.45;
      next = Math.min(endpointVol, next + rate * dt);
      deliveredRef.current = next;
      setDelivered(next);
      if (next >= endpointVol - 0.001) setPlaying(false);
    } else if (kind === 'gravimetric') {
      const next = Math.min(1, settleRef.current + 0.35 * dt);
      settleRef.current = next;
      setSettle(next);
      if (next >= 1) setPlaying(false);
    } else {
      // observation: just a short timed reaction, then stop
      const next = Math.min(1, settleRef.current + 0.5 * dt);
      settleRef.current = next;
      setSettle(next);
      if (next >= 1) setPlaying(false);
    }
  }, playing && !finished);

  // ── checkpoint gating ──────────────────────────────────────────────────────
  // A step with a checkpoint can only be completed once its gate is satisfied.
  const stepGateMet = useCallback(
    (i: number): boolean => {
      const step = steps[i];
      if (!step?.checkpoint) return true;
      // The gate is satisfied by the archetype's primary live event.
      if (kind === 'titration') return endpointReached;
      if (kind === 'gravimetric') return settled;
      return settle >= 1; // observation: reaction watched
    },
    [steps, kind, endpointReached, settled, settle],
  );

  // ── result computation ─────────────────────────────────────────────────────
  const dataColumns = config.dataColumns ?? defaultColumns(kind);
  const [dataRows, setDataRows] = useState<DataRow[]>(() => seedRows(config, kind));

  // Auto-fill the auto columns from the live simulation when the gate fires.
  useEffect(() => {
    if (kind === 'titration' && endpointReached) {
      setDataRows((rows) =>
        rows.map((r, i) =>
          i === 0 ? { ...r, values: { ...r.values, vol: round(delivered, 2) } } : r,
        ),
      );
    }
  }, [kind, endpointReached, delivered]);

  useEffect(() => {
    if (kind === 'gravimetric' && settled) {
      setDataRows((rows) =>
        rows.map((r, i) =>
          i === 0
            ? { ...r, values: { ...r.values, gross: round(filterMass + trueppt, 4), tare: round(filterMass, 4) } }
            : r,
        ),
      );
    }
  }, [kind, settled, filterMass, trueppt]);

  const resultSpec = config.result ?? {};
  const computed = useMemo(
    () => computeResult(kind, resultSpec, dataColumns, dataRows, { analyteVol }),
    [kind, resultSpec, dataColumns, dataRows, analyteVol],
  );
  const expected = resultSpec.expected ?? null;
  const precision = resultSpec.precision ?? 3;
  const tolerancePct = resultSpec.tolerancePct ?? 5;
  const errorPct =
    computed != null && expected != null && expected !== 0
      ? Math.abs((computed - expected) / expected) * 100
      : null;
  const passed = errorPct != null ? errorPct <= tolerancePct : null;

  // ── completion ─────────────────────────────────────────────────────────────
  const allStepsDone = steps.length > 0 && done.size === steps.length;

  useEffect(() => {
    if (allStepsDone && !completedRef.current) {
      completedRef.current = true;
      setFinished(true);
      onComplete?.({
        stepsCompleted: done.size,
        total: steps.length,
        computed,
        expected,
        errorPct,
        passed,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStepsDone]);

  // ── step interaction ───────────────────────────────────────────────────────
  const completeStep = useCallback(
    (i: number) => {
      setDone((prev) => {
        if (prev.has(i)) return prev;
        const next = new Set(prev);
        next.add(i);
        return next;
      });
      // advance focus to the next not-done step
      setActive((cur) => (i >= cur ? Math.min(steps.length - 1, i + 1) : cur));
    },
    [steps.length],
  );

  const toggleStep = useCallback(
    (i: number) => {
      // can't tick a gated step until its checkpoint is met
      if (!done.has(i) && !stepGateMet(i)) return;
      setDone((prev) => {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i);
        else next.add(i);
        return next;
      });
      setActive(i);
    },
    [done, stepGateMet],
  );

  const reset = useCallback(() => {
    setDone(new Set());
    setActive(0);
    setPlaying(false);
    setFinished(false);
    completedRef.current = false;
    setDelivered(0);
    deliveredRef.current = 0;
    setSettle(0);
    settleRef.current = 0;
    setDataRows(seedRows(config, kind));
  }, [config, kind]);

  const runStep = useCallback(() => {
    // run the active step's apparatus animation
    if (kind === 'titration' && deliveredRef.current >= endpointVol - 0.001) {
      setDelivered(0);
      deliveredRef.current = 0;
    }
    if ((kind === 'gravimetric' || kind === 'observation') && settleRef.current >= 1) {
      setSettle(0);
      settleRef.current = 0;
    }
    setPlaying((p) => !p);
  }, [kind, endpointVol]);

  // ── self-demo (no cue): walk the first couple of steps + run apparatus ──────
  const demo = useMountSequence(
    [
      { at: 400, run: () => setActive(0) },
      { at: 900, run: () => { if (steps.length > 0) setPlaying(true); } },
      { at: 2600, run: () => setPlaying(false) },
    ],
    {
      skip: reduced || hasCue || steps.length === 0,
      onSettle: () => setPlaying(false),
    },
  );
  const cancelDemo = useCallback(() => {
    if (demo.running) demo.cancel();
  }, [demo]);

  // ── cue handling ───────────────────────────────────────────────────────────
  // step → jump active step + mark prior steps done.
  useEffect(() => {
    if (cue?.step == null || steps.length === 0) return;
    const idx = Math.max(0, Math.min(steps.length - 1, Math.round(cue.step)));
    setActive(idx);
    setDone((prev) => {
      const next = new Set(prev);
      for (let i = 0; i < idx; i++) next.add(i);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.step]);

  // progress → derive active step across the procedure.
  useEffect(() => {
    if (cue?.progress == null || steps.length === 0) return;
    const idx = Math.min(steps.length - 1, Math.floor(clamp01(cue.progress) * steps.length));
    setActive(idx);
    setDone((prev) => {
      const next = new Set(prev);
      for (let i = 0; i < idx; i++) next.add(i);
      return next;
    });
  }, [cue?.progress, steps.length]);

  // play → run the apparatus animation.
  useEffect(() => {
    if (cue?.play == null) return;
    setPlaying(Boolean(cue.play));
  }, [cue?.play]);

  // highlight → spotlight a step / apparatus.
  const highlights = cueHighlights(cue);
  const isApparatusHighlighted = useCallback(
    (spec: ApparatusSpec, i: number): boolean => {
      if (highlights.length === 0) return false;
      return highlights.some(
        (h) =>
          h === spec.id?.toLowerCase() ||
          h === spec.type ||
          h === String(i) ||
          (spec.caption ? spec.caption.toLowerCase().includes(h) : false),
      );
    },
    [highlights],
  );
  const isStepHighlighted = useCallback(
    (step: ExperimentStep, i: number): boolean => {
      if (highlights.length === 0) return i === active;
      return highlights.some(
        (h) => h === step.id?.toLowerCase() || h === String(i) || step.text.toLowerCase().includes(h),
      );
    },
    [highlights, active],
  );

  // ── derived UI bits ────────────────────────────────────────────────────────
  const activeStep = steps[active];
  const activeSafety = activeStep?.safety;
  const standingSafety = safetyNotes[0];
  const benchSafety = activeSafety ?? standingSafety;

  const statusTone = finished
    ? passed === false
      ? 'warning'
      : 'success'
    : playing
      ? 'active'
      : 'idle';

  const phaseLabel =
    kind === 'titration'
      ? endpointReached
        ? 'Endpoint reached'
        : playing
          ? 'Titrating…'
          : `${delivered.toFixed(2)} / ${endpointVol.toFixed(2)} mL`
      : kind === 'gravimetric'
        ? settled
          ? 'Dried & weighed'
          : playing
            ? 'Settling…'
            : `${Math.round(settle * 100)}%`
        : playing
          ? 'Reacting…'
          : settle >= 1
            ? 'Reaction complete'
            : 'Ready';

  const settlingBar = kind === 'gravimetric' ? settle : kind === 'observation' ? settle : undefined;

  return (
    <div className="grid w-full flex-1 grid-cols-1 gap-4 lg:min-h-0 lg:grid-cols-[minmax(0,1.7fr)_minmax(20rem,1fr)]">
      {/* ── LEFT: bench stage ──────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-col lg:overflow-y-auto">
        <LabBench
          experimentName={config.name ?? 'Experiment'}
          completedCount={done.size}
          totalSteps={steps.length}
          currentStepLabel={activeStep ? `Step ${active + 1}: ${activeStep.text}` : undefined}
          phase={phaseLabel}
          statusTone={statusTone}
          safetyNote={benchSafety}
          settlingProgress={settlingBar}
          footer={
            <ApparatusControls
              kind={kind}
              playing={playing}
              endpointReached={endpointReached}
              settled={settled}
              onRun={() => { cancelDemo(); runStep(); }}
              onReset={() => { cancelDemo(); reset(); }}
              hasApparatus={apparatus.length > 0}
            />
          }
        >
          {apparatus.length > 0 ? (
            <div className="relative flex max-h-[360px] flex-wrap items-end justify-center gap-6">
              <NowYouTry show={demo.done && !reduced && !hasCue} />
              {apparatus.map((a, i) => renderApparatus(a, i, live, isApparatusHighlighted(a, i)))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <BeakerIcon size={28} className="text-ws-ink-4" />
              <p className="text-sm text-ws-ink-3">No apparatus configured for this experiment.</p>
            </div>
          )}
        </LabBench>

        {/* Live observation panel */}
        {activeStep?.observation && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-ws-border bg-ws-surface p-3 text-sm">
            <Sparkles size={15} className="mt-0.5 flex-shrink-0 text-ws-accent" />
            <div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">Observation</div>
              <p className="text-ws-ink-2">{activeStep.observation}</p>
            </div>
          </div>
        )}
      </div>

      {/* ── RIGHT: procedure / data / result rail ──────────────────────────── */}
      <div className="flex min-h-0 min-w-0 flex-col gap-4 pb-2 lg:overflow-y-auto">
        {config.objective && (
          <div className="rounded-md border border-ws-border bg-ws-accent-quiet px-3 py-2 text-xs text-ws-accent-ink">
            <span className="font-semibold">Objective: </span>
            {config.objective}
          </div>
        )}

        {/* Procedure */}
        {steps.length > 0 && (
          <div className="rounded-md border border-ws-border bg-ws-surface p-3">
            <div className="mb-2 flex items-center gap-1.5">
              <ClipboardList size={14} className="text-ws-ink-3" />
              <h4 className="text-sm font-semibold text-ws-ink">Procedure</h4>
            </div>
            <ol className="flex flex-col gap-0.5">
              {steps.map((step, i) => {
                const isDone = done.has(i);
                const gateMet = stepGateMet(i);
                const gated = !isDone && !gateMet;
                const isActive = isStepHighlighted(step, i);
                return (
                  <li key={step.id ?? i}>
                    <button
                      type="button"
                      onClick={() => { cancelDemo(); toggleStep(i); }}
                      disabled={gated}
                      aria-current={isActive ? 'step' : undefined}
                      className={`flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors ${VIZ_FOCUS} ${
                        gated ? 'cursor-not-allowed opacity-60' : 'hover:bg-ws-surface-2'
                      } ${isActive && !isDone ? 'bg-ws-accent-quiet ring-1 ring-ws-accent' : ''}`}
                    >
                      <span className="mt-0.5 flex-shrink-0">
                        {isDone ? (
                          <CheckCircle2 size={16} className="text-ws-success" />
                        ) : gated ? (
                          <Lock size={16} className="text-ws-ink-4" />
                        ) : (
                          <Circle size={16} className={isActive ? 'text-ws-accent' : 'text-ws-ink-4'} />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={isDone ? 'text-ws-ink-3 line-through' : 'text-ws-ink'}>{step.text}</span>
                        {step.detail && (
                          <span className="mt-0.5 block text-xs text-ws-ink-3">{step.detail}</span>
                        )}
                        {step.checkpoint && (
                          <span
                            className={`mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                              gateMet
                                ? 'bg-ws-success/15 text-ws-success'
                                : 'bg-ws-warning/15 text-ws-warning'
                            }`}
                          >
                            {gateMet ? <CheckCircle2 size={10} /> : <Lock size={10} />}
                            {gateMet ? 'Checkpoint met' : step.checkpoint}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {!allStepsDone && (
              <p className="mt-2 px-2 text-[11px] text-ws-ink-3">
                Tick each step as you complete it. Gated steps unlock once their checkpoint is met on the bench.
              </p>
            )}
            {allStepsDone && (
              <div className="mt-2 flex items-center gap-1.5 rounded-sm border border-ws-success bg-ws-success/10 px-3 py-1.5 text-xs font-medium text-ws-success">
                <ChevronRight size={14} /> All steps complete
              </div>
            )}
          </div>
        )}

        {/* Data collection table */}
        {dataColumns.length > 0 && (
          <DataTable
            columns={dataColumns}
            rows={dataRows}
            onChangeCell={(rowIdx, key, value) =>
              setDataRows((rows) =>
                rows.map((r, i) => (i === rowIdx ? { ...r, values: { ...r.values, [key]: value } } : r)),
              )
            }
          />
        )}

        {/* Result vs expected */}
        {(resultSpec.label || expected != null || computed != null) && (
          <ResultCard
            label={resultSpec.label ?? 'Result'}
            computed={computed}
            expected={expected}
            unit={resultSpec.unit}
            precision={precision}
            errorPct={errorPct}
            passed={passed}
            tolerancePct={tolerancePct}
          />
        )}

        {/* Safety notes */}
        {safetyNotes.length > 0 && (
          <div className="rounded-md border border-ws-warning/40 bg-ws-warning/5 p-3">
            <div className="mb-1.5 flex items-center gap-1.5">
              <ShieldAlert size={14} className="text-ws-warning" />
              <h4 className="text-sm font-semibold text-ws-ink">Safety</h4>
            </div>
            <ul className="flex flex-col gap-1">
              {safetyNotes.map((s, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs text-ws-ink-2">
                  <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-ws-warning" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Completion summary */}
        {finished && (
          <CompletionSummary
            name={config.name ?? 'Experiment'}
            steps={steps.length}
            computed={computed}
            expected={expected}
            unit={resultSpec.unit}
            precision={precision}
            errorPct={errorPct}
            passed={passed}
            onReset={reset}
          />
        )}

        {aside}
      </div>
    </div>
  );
}

// ── apparatus footer controls ────────────────────────────────────────────────

function ApparatusControls({
  kind,
  playing,
  endpointReached,
  settled,
  onRun,
  onReset,
  hasApparatus,
}: {
  kind: ExperimentKind;
  playing: boolean;
  endpointReached: boolean;
  settled: boolean;
  onRun: () => void;
  onReset: () => void;
  hasApparatus: boolean;
}) {
  if (!hasApparatus) {
    return <p className="text-center text-[11px] text-ws-ink-3">Configure apparatus to enable the bench controls.</p>;
  }
  const runLabel =
    kind === 'titration'
      ? endpointReached
        ? 'Replay titration'
        : playing
          ? 'Pause'
          : 'Open stopcock'
      : kind === 'gravimetric'
        ? settled
          ? 'Replay'
          : playing
            ? 'Pause'
            : 'Start settling'
        : playing
          ? 'Pause'
          : 'Run reaction';
  return (
    <div className="flex items-center justify-center gap-2">
      <button type="button" onClick={onRun} className={VIZ_BTN_PRIMARY}>
        {playing ? <Pause size={13} /> : <Play size={13} />}
        {runLabel}
      </button>
      <button type="button" onClick={onReset} className={VIZ_BTN_GHOST}>
        <RotateCcw size={13} />
        Reset bench
      </button>
    </div>
  );
}

// ── data collection table ────────────────────────────────────────────────────

function DataTable({
  columns,
  rows,
  onChangeCell,
}: {
  columns: DataColumn[];
  rows: DataRow[];
  onChangeCell: (rowIdx: number, key: string, value: number | string) => void;
}) {
  return (
    <div className="rounded-md border border-ws-border bg-ws-surface p-3">
      <h4 className="mb-2 text-sm font-semibold text-ws-ink">Data collection</h4>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-ws-border text-ws-ink-3">
              <th className="px-2 py-1.5 text-left font-medium">Trial</th>
              {columns.map((c) => (
                <th key={c.key} className="px-2 py-1.5 text-right font-medium">
                  {c.label}
                  {c.unit && <span className="ml-0.5 font-normal text-ws-ink-4">({c.unit})</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="border-b border-ws-border/60 last:border-0">
                <td className="px-2 py-1.5 text-ws-ink-2">{row.label ?? `Trial ${ri + 1}`}</td>
                {columns.map((c) => {
                  const v = row.values?.[c.key];
                  const isEntry = c.source !== 'auto';
                  return (
                    <td key={c.key} className="px-2 py-1 text-right">
                      {isEntry ? (
                        <input
                          type="number"
                          inputMode="decimal"
                          value={v ?? ''}
                          onChange={(e) => onChangeCell(ri, c.key, e.target.value === '' ? '' : parseFloat(e.target.value))}
                          className={`w-20 rounded-sm border border-ws-border bg-ws-tile px-1.5 py-1 text-right font-mono tabular-nums text-ws-ink focus:border-ws-accent focus:outline-none ${VIZ_FOCUS}`}
                          aria-label={`${row.label ?? `Trial ${ri + 1}`} ${c.label}`}
                        />
                      ) : (
                        <span className="font-mono tabular-nums text-ws-ink">
                          {v != null && v !== '' ? v : <span className="text-ws-ink-4">—</span>}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[10px] text-ws-ink-3">
        Auto columns fill from the bench when a checkpoint fires; enter the rest yourself.
      </p>
    </div>
  );
}

// ── result card ──────────────────────────────────────────────────────────────

function ResultCard({
  label,
  computed,
  expected,
  unit,
  precision,
  errorPct,
  passed,
  tolerancePct,
}: {
  label: string;
  computed: number | null;
  expected: number | null;
  unit?: string;
  precision: number;
  errorPct: number | null;
  passed: boolean | null;
  tolerancePct: number;
}) {
  return (
    <div className="rounded-md border border-ws-border bg-ws-surface p-3">
      <h4 className="mb-2 text-sm font-semibold text-ws-ink">{label}</h4>
      <div className="grid grid-cols-2 gap-2">
        <ResultStat label="Your result" value={fmt(computed, precision, unit)} />
        <ResultStat label="Expected" value={fmt(expected, precision, unit)} />
      </div>
      {errorPct != null && (
        <div
          className={`mt-2 flex items-center justify-between rounded-sm px-3 py-2 text-sm font-medium ${
            passed ? 'bg-ws-success/12 text-ws-success' : 'bg-ws-danger/12 text-ws-danger'
          }`}
        >
          <span className="flex items-center gap-1.5">
            {passed ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
            {passed ? 'Within tolerance' : 'Out of tolerance'}
          </span>
          <span className="font-mono tabular-nums">
            {errorPct.toFixed(2)}% error <span className="text-ws-ink-3">(±{tolerancePct}%)</span>
          </span>
        </div>
      )}
      {errorPct == null && (
        <p className="mt-2 text-[11px] text-ws-ink-3">
          Complete the bench run and fill the data table to compute your result.
        </p>
      )}
    </div>
  );
}

function ResultStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-ws-border bg-ws-tile px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-ws-ink-3">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums text-ws-ink">{value}</div>
    </div>
  );
}

// ── completion summary ───────────────────────────────────────────────────────

function CompletionSummary({
  name,
  steps,
  computed,
  expected,
  unit,
  precision,
  errorPct,
  passed,
  onReset,
}: {
  name: string;
  steps: number;
  computed: number | null;
  expected: number | null;
  unit?: string;
  precision: number;
  errorPct: number | null;
  passed: boolean | null;
  onReset: () => void;
}) {
  const tone = passed === false ? 'warning' : 'success';
  return (
    <div
      className={`rounded-md border p-4 ${
        tone === 'success' ? 'border-ws-success bg-ws-success/8' : 'border-ws-warning bg-ws-warning/8'
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <Trophy size={18} className={tone === 'success' ? 'text-ws-success' : 'text-ws-warning'} />
        <h4 className="text-sm font-semibold text-ws-ink">Experiment complete</h4>
      </div>
      <p className="text-xs text-ws-ink-2">
        You worked through all {steps} steps of <span className="font-medium text-ws-ink">{name}</span>.
        {computed != null && (
          <>
            {' '}Your measured result was <span className="font-mono font-semibold text-ws-ink">{fmt(computed, precision, unit)}</span>
            {expected != null && (
              <> against an expected <span className="font-mono font-semibold text-ws-ink">{fmt(expected, precision, unit)}</span></>
            )}
            {errorPct != null && (
              <> — {passed ? 'an excellent result well within' : 'outside'} tolerance at {errorPct.toFixed(2)}% error.</>
            )}
          </>
        )}
      </p>
      <button type="button" onClick={onReset} className={`${VIZ_BTN_GHOST} mt-3`}>
        <RotateCcw size={13} />
        Run again
      </button>
    </div>
  );
}

// ── result / data defaults ───────────────────────────────────────────────────

function defaultColumns(kind: ExperimentKind): DataColumn[] {
  switch (kind) {
    case 'titration':
      return [{ key: 'vol', label: 'Titrant volume', unit: 'mL', source: 'auto' }];
    case 'gravimetric':
      return [
        { key: 'gross', label: 'Paper + ppt', unit: 'g', source: 'auto' },
        { key: 'tare', label: 'Paper (tare)', unit: 'g', source: 'auto' },
      ];
    default:
      return [];
  }
}

function seedRows(config: ExperimentConfig, kind: ExperimentKind): DataRow[] {
  if (config.dataRows && config.dataRows.length > 0) {
    return config.dataRows.map((r) => ({ ...r, values: { ...r.values } }));
  }
  if (kind === 'titration' || kind === 'gravimetric') return [{ label: 'Trial 1', values: {} }];
  return [];
}

function computeResult(
  kind: ExperimentKind,
  spec: ResultSpec,
  columns: DataColumn[],
  rows: DataRow[],
  ctx: { analyteVol: number },
): number | null {
  const formula = spec.formula ?? defaultFormula(kind);

  if (formula === 'titration_molarity') {
    // C_acid = (C_base · V_base) / V_acid — uses expected as titrant conc if given.
    const v = firstNumber(rows, 'vol');
    if (v == null) return null;
    // titrant conc encoded via expected back-solve isn't reliable; use the
    // standard relation with the known analyte volume and a 0.1 M titrant unless
    // the config result expresses otherwise. Keep simple + transparent:
    const titrantConc = 0.1;
    if (ctx.analyteVol <= 0) return null;
    return (titrantConc * v) / ctx.analyteVol;
  }

  if (formula === 'mass_by_difference') {
    const gross = firstNumber(rows, 'gross');
    const tare = firstNumber(rows, 'tare');
    if (gross == null || tare == null) return null;
    return gross - tare;
  }

  // generic: collect the first numeric column's values across rows
  const key = columns[0]?.key;
  if (!key) return null;
  const vals = rows.map((r) => toNum(r.values?.[key])).filter((n): n is number => n != null);
  if (vals.length === 0) return null;
  if (formula === 'last') return vals[vals.length - 1];
  // mean
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function defaultFormula(kind: ExperimentKind): NonNullable<ResultSpec['formula']> {
  if (kind === 'titration') return 'titration_molarity';
  if (kind === 'gravimetric') return 'mass_by_difference';
  return 'mean';
}

function firstNumber(rows: DataRow[], key: string): number | null {
  for (const r of rows) {
    const n = toNum(r.values?.[key]);
    if (n != null) return n;
  }
  return null;
}

function toNum(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v !== '' ? parseFloat(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

function round(v: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(v * f) / f;
}

function fmt(v: number | null, precision: number, unit?: string): string {
  if (v == null) return '—';
  return `${v.toFixed(precision)}${unit ? ` ${unit}` : ''}`;
}
